<template>
  <div class="analytics-page">
    <header class="analytics-page__header">
      <div>
        <h1>{{ $t('analytics.title') }}</h1>
        <p>{{ $t('analytics.subtitle') }}</p>
      </div>
      <div class="analytics-page__tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="analytics-page__tab"
          :class="{ 'analytics-page__tab--active': activeTab === tab.id }"
          @click="changeTab(tab.id)"
        >
          {{ $t(`analytics.${tab.id}`) }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="analytics-page__loading">{{ $t('analytics.loading') }}</div>

    <template v-else-if="activeTab === 'overview' && dashboard">
      <section class="kpi-grid">
        <article class="kpi-card kpi-card--accent">
          <span>{{ $t('analytics.todayRevenue') }}</span>
          <strong>{{ formatMoney(dashboard.today.revenue) }}</strong>
          <em :class="changeClass(dashboard.changes.revenue_vs_yesterday)">
            {{ formatChange(dashboard.changes.revenue_vs_yesterday) }} {{ $t('analytics.vsYesterday') }}
          </em>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.todayReceipts') }}</span>
          <strong>{{ dashboard.today.orders }}</strong>
          <em :class="changeClass(dashboard.changes.orders_vs_yesterday)">
            {{ formatChange(dashboard.changes.orders_vs_yesterday) }} {{ $t('analytics.vsYesterday') }}
          </em>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.days30Profit') }}</span>
          <strong>{{ formatMoney(dashboard.month.profit) }}</strong>
          <em>{{ $t('analytics.margin', { value: dashboard.insights.margin_percent }) }}</em>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.averageReceipt') }}</span>
          <strong>{{ formatMoney(dashboard.insights.avg_check) }}</strong>
          <em>{{ $t('analytics.receiptsForDays', { count: dashboard.month.orders }) }}</em>
        </article>
      </section>

      <section class="insight-row">
        <article v-if="dashboard.insights.best_day" class="insight-card">
          <i class="mdi mdi-calendar-star"></i>
          <div>
            <h4>{{ $t('analytics.bestDay') }}</h4>
            <p>
              {{ dashboard.insights.best_day.label }} —
              {{ formatMoney(dashboard.insights.best_day.revenue) }}
            </p>
          </div>
        </article>
        <article v-if="dashboard.insights.best_hour" class="insight-card">
          <i class="mdi mdi-clock-outline"></i>
          <div>
            <h4>{{ $t('analytics.salesPeak') }}</h4>
            <p>
              {{ $t('analytics.around', { time: dashboard.insights.best_hour.label }) }}
              {{ $t('analytics.receiptsToday', { count: dashboard.insights.best_hour.orders }) }}
            </p>
          </div>
        </article>
        <article v-if="dashboard.insights.top_product" class="insight-card">
          <i class="mdi mdi-trophy-outline"></i>
          <div>
            <h4>{{ $t('analytics.salesHit') }}</h4>
            <p>
              {{ dashboard.insights.top_product.name }} —
              {{ dashboard.insights.top_product.sold_qty }} {{ $t('analytics.units') }}
            </p>
          </div>
        </article>
        <article class="insight-card">
          <i class="mdi mdi-chart-timeline-variant"></i>
          <div>
            <h4>{{ $t('analytics.weekToWeek') }}</h4>
            <p :class="changeClass(dashboard.changes.revenue_vs_prev_week)">
              {{ formatChange(dashboard.changes.revenue_vs_prev_week) }} {{ $t('analytics.revenue') }}
            </p>
          </div>
        </article>
      </section>

      <section class="charts-grid">
        <article class="panel panel--wide">
          <div class="panel__head">
            <h3>{{ $t('analytics.revenue14Days') }}</h3>
            <span>{{ $t('analytics.revenueForDays', { amount: formatMoney(dashboard.month.revenue) }) }}</span>
          </div>
          <apexchart
            type="area"
            height="280"
            :options="trendOptions"
            :series="trendSeries"
          />
        </article>

        <article class="panel">
          <div class="panel__head">
            <h3>{{ $t('analytics.paymentTypes') }}</h3>
            <span>{{ $t('analytics.days30') }}</span>
          </div>
          <apexchart
            v-if="paymentSeries.length"
            type="donut"
            height="280"
            :options="paymentOptions"
            :series="paymentSeries"
          />
          <div v-else class="panel__empty">{{ $t('analytics.noPayments') }}</div>
        </article>
      </section>

      <section class="charts-grid">
        <article class="panel">
          <div class="panel__head">
            <h3>{{ $t('analytics.topProducts') }}</h3>
            <div class="panel__switch">
              <button
                type="button"
                :class="{ active: topProductsMode === 'qty' }"
                @click="topProductsMode = 'qty'"
              >
                {{ $t('analytics.byQuantity') }}
              </button>
              <button
                type="button"
                :class="{ active: topProductsMode === 'revenue' }"
                @click="topProductsMode = 'revenue'"
              >
                {{ $t('analytics.byRevenue') }}
              </button>
            </div>
          </div>
          <div v-if="topProducts.length" class="rank-list">
            <div
              v-for="(product, index) in topProducts"
              :key="product.id"
              class="rank-list__item"
            >
              <span class="rank-list__pos" :class="{ 'rank-list__pos--gold': index === 0 }">
                {{ index + 1 }}
              </span>
              <div class="rank-list__body">
                <strong>{{ product.name }}</strong>
                <div class="rank-list__bar">
                  <i :style="{ width: productBarWidth(product) }"></i>
                </div>
              </div>
              <div class="rank-list__meta">
                <b v-if="topProductsMode === 'qty'">{{ product.sold_qty }} {{ $t('analytics.soldShort') }}</b>
                <b v-else>{{ formatMoney(product.revenue) }}</b>
                <small v-if="topProductsMode === 'qty'">{{ formatMoney(product.revenue) }}</small>
                <small v-else>{{ product.sold_qty }} {{ $t('analytics.soldShort') }} · {{ $t('analytics.profit') }} {{ formatMoney(product.profit) }}</small>
              </div>
            </div>
          </div>
          <div v-else class="panel__empty">{{ $t('analytics.noSalesPeriod') }}</div>
        </article>

        <article class="panel">
          <div class="panel__head">
            <h3>{{ $t('analytics.hourlyActivity') }}</h3>
            <span>{{ $t('analytics.days30') }}</span>
          </div>
          <apexchart
            type="bar"
            height="280"
            :options="hoursOptions"
            :series="hoursSeries"
          />
        </article>
      </section>

      <section v-if="cashiers.length" class="panel">
        <div class="panel__head">
          <h3>{{ $t('analytics.cashierRevenue') }}</h3>
          <span>{{ $t('analytics.days30') }} · {{ $t('analytics.today') }}</span>
        </div>
        <div class="cashier-list">
          <div
            v-for="(cashier, index) in cashiers"
            :key="cashier.user_id || index"
            class="cashier-list__item"
          >
            <span class="cashier-list__pos">{{ index + 1 }}</span>
            <div class="cashier-list__body">
              <strong>{{ cashier.name }}</strong>
              <div class="cashier-list__bar">
                <i :style="{ width: cashierBarWidth(cashier) }"></i>
              </div>
              <small>{{ $t('analytics.receiptsForDays', { count: cashier.orders }) }}</small>
            </div>
            <div class="cashier-list__meta">
              <b>{{ formatMoney(cashier.revenue) }}</b>
              <small v-if="cashierToday(cashier)">
                {{ $t('analytics.today') }} {{ formatMoney(cashierToday(cashier).revenue) }} · {{ $t('analytics.receiptsToday', { count: cashierToday(cashier).orders }) }}
              </small>
              <small v-else>{{ $t('analytics.todayZero') }}</small>
            </div>
          </div>
        </div>
      </section>

      <section v-if="dashboard.low_stock.length" class="panel">
        <div class="panel__head">
          <h3>{{ $t('analytics.lowStock') }}</h3>
          <span>{{ $t('analytics.units5') }}</span>
        </div>
        <div class="stock-grid">
          <div
            v-for="item in dashboard.low_stock"
            :key="item.id"
            class="stock-chip"
          >
            <strong>{{ item.name }}</strong>
            <span>{{ item.quantity }}</span>
          </div>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 'week'">
      <section class="kpi-grid kpi-grid--3">
        <article class="kpi-card">
          <span>{{ $t('analytics.ordersWeek') }}</span>
          <strong>{{ weekly.total_orders || 0 }}</strong>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.revenue') }}</span>
          <strong>{{ formatMoney(weekly.total_revenue) }}</strong>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.profitLabel') }}</span>
          <strong>{{ formatMoney(weekly.net_profit) }}</strong>
        </article>
      </section>

      <div v-if="statistics.length" class="period-grid">
        <article v-for="(day, index) in statistics" :key="index" class="period-card">
          <h3>{{ day.weekday }}</h3>
          <div
            v-for="(payment, i) in day.payment_types"
            :key="i"
            class="period-card__row"
          >
            <span class="period-card__badge">{{ payment.payment_type }}</span>
            <div>
              <p>{{ $t('analytics.sold', { count: payment.total_orders }) }}</p>
              <p>{{ $t('analytics.revenueLabel', { amount: formatMoney(payment.total_revenue) }) }}</p>
              <p>{{ $t('analytics.profitLabelValue', { amount: formatMoney(payment.net_profit) }) }}</p>
            </div>
          </div>
        </article>
      </div>
      <div v-else class="analytics-page__empty">{{ $t('analytics.noWeekData') }}</div>
    </template>

    <template v-else-if="activeTab === 'year'">
      <section class="kpi-grid kpi-grid--3">
        <article class="kpi-card">
          <span>{{ $t('analytics.ordersYear') }}</span>
          <strong>{{ yearly.total_orders || 0 }}</strong>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.revenue') }}</span>
          <strong>{{ formatMoney(yearly.total_revenue) }}</strong>
        </article>
        <article class="kpi-card">
          <span>{{ $t('analytics.profitLabel') }}</span>
          <strong>{{ formatMoney(yearly.net_profit) }}</strong>
        </article>
      </section>

      <div v-if="months.length" class="period-grid">
        <article v-for="(month, index) in months" :key="index" class="period-card">
          <h3>{{ month.month }}</h3>
          <div
            v-for="(payment, i) in month.payment_types"
            :key="i"
            class="period-card__row"
          >
            <span class="period-card__badge">{{ payment.payment_type }}</span>
            <div>
              <p>{{ $t('analytics.sold', { count: payment.total_orders }) }}</p>
              <p>{{ $t('analytics.revenueLabel', { amount: formatMoney(payment.total_revenue) }) }}</p>
            </div>
          </div>
        </article>
      </div>
      <div v-else class="analytics-page__empty">{{ $t('analytics.noYearData') }}</div>
    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  getSalesAnalytics,
  getSalesMonth,
  getSalesWeek,
} from '@/api/sales'
import { formatMoney } from '@/utils/format'
import { notifyError } from '@/utils/notify'

export default {
  name: 'Analytic',
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      activeTab: 'overview',
      tabs: [
        { id: 'overview' },
        { id: 'week' },
        { id: 'year' },
      ],
      dashboard: null,
      statistics: [],
      weekly: {},
      months: [],
      yearly: {},
      topProductsMode: 'qty',
      formatMoney,
    }
  },
  computed: {
    topProducts() {
      const list = [...((this.dashboard && this.dashboard.top_products) || [])]
      if (this.topProductsMode === 'revenue') {
        list.sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0))
      } else {
        list.sort((a, b) => (Number(b.sold_qty) || 0) - (Number(a.sold_qty) || 0))
      }
      return list.slice(0, 10)
    },
    maxSold() {
      const key = this.topProductsMode === 'revenue' ? 'revenue' : 'sold_qty'
      return Math.max(...this.topProducts.map((p) => Number(p[key]) || 0), 1)
    },
    cashiers() {
      return (this.dashboard && this.dashboard.cashiers) || []
    },
    maxCashierRevenue() {
      return Math.max(...this.cashiers.map((c) => Number(c.revenue) || 0), 1)
    },
    trendSeries() {
      const points = (this.dashboard && this.dashboard.daily_trend) || []
      return [
        {
          name: this.$t('analytics.revenue'),
          data: points.map((p) => Number(p.revenue) || 0),
        },
      ]
    },
    trendOptions() {
      const points = (this.dashboard && this.dashboard.daily_trend) || []
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit',
        },
        colors: ['#0b4f8a'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        grid: {
          borderColor: '#e8eef5',
          strokeDashArray: 4,
        },
        xaxis: {
          categories: points.map((p) => p.label),
          labels: { style: { colors: '#6b7a90' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#6b7a90' },
            formatter: (val) => this.shortMoney(val),
          },
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatMoney(val),
          },
        },
      }
    },
    paymentSeries() {
      return ((this.dashboard && this.dashboard.payments) || []).map((p) => Number(p.amount) || 0)
    },
    paymentOptions() {
      const payments = (this.dashboard && this.dashboard.payments) || []
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        labels: payments.map((p) => p.label),
        colors: ['#0b4f8a', '#d97706', '#0f766e', '#b45309', '#334155', '#0369a1'],
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: this.$t('analytics.total'),
                  formatter: () => this.shortMoney(
                    payments.reduce((sum, p) => sum + Number(p.amount || 0), 0)
                  ),
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatMoney(val),
          },
        },
      }
    },
    hoursSeries() {
      const hours = (this.dashboard && this.dashboard.peak_hours) || []
      return [
        {
          name: this.$t('analytics.receipts'),
          data: hours.map((h) => Number(h.orders) || 0),
        },
      ]
    },
    hoursOptions() {
      const hours = (this.dashboard && this.dashboard.peak_hours) || []
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#0f766e'],
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '70%',
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: '#e8eef5',
          strokeDashArray: 4,
        },
        xaxis: {
          categories: hours.map((h) => h.label),
          labels: {
            style: { colors: '#6b7a90', fontSize: '10px' },
            rotate: -45,
            hideOverlappingLabels: true,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: { style: { colors: '#6b7a90' } },
        },
        tooltip: {
          y: {
            formatter: (val) => this.$t('analytics.receiptTooltip', { value: val }),
          },
        },
      }
    },
  },
  mounted() {
    this.loadOverview()
  },
  methods: {
    shortMoney(value) {
      const amount = Number(value) || 0
      if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`
      if (amount >= 1000) return `${Math.round(amount / 1000)}K`
      return `${Math.round(amount)}`
    },
    formatChange(value) {
      const num = Number(value) || 0
      const sign = num > 0 ? '+' : ''
      return `${sign}${num}%`
    },
    changeClass(value) {
      const num = Number(value) || 0
      if (num > 0) return 'is-up'
      if (num < 0) return 'is-down'
      return 'is-flat'
    },
    productBarWidth(product) {
      const key = this.topProductsMode === 'revenue' ? 'revenue' : 'sold_qty'
      return `${Math.max(8, (Number(product[key]) / this.maxSold) * 100)}%`
    },
    cashierBarWidth(cashier) {
      return `${Math.max(8, (Number(cashier.revenue) / this.maxCashierRevenue) * 100)}%`
    },
    cashierToday(cashier) {
      const list = (this.dashboard && this.dashboard.cashiers_today) || []
      return list.find((row) => row.user_id === cashier.user_id) || null
    },
    changeTab(id) {
      this.activeTab = id
      if (id === 'overview') this.loadOverview()
      if (id === 'week') this.loadWeek()
      if (id === 'year') this.loadMonth()
    },
    async loadOverview() {
      this.loading = true
      try {
        this.dashboard = await getSalesAnalytics()
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    async loadWeek() {
      this.loading = true
      try {
        const data = await getSalesWeek()
        this.statistics = data.data || []
        this.weekly = data.weekly_totals || {}
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    async loadMonth() {
      this.loading = true
      try {
        const data = await getSalesMonth()
        this.months = data.data || []
        this.yearly = data.monthly_totals || {}
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.analytics-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.analytics-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.analytics-page__tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.analytics-page__tab {
  padding: 10px 14px;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #fff;
  color: #3d4f63;
  cursor: pointer;
  font-weight: 650;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0b4f8a;
    color: #0b4f8a;
  }

  &--active {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;
  }
}

.analytics-page__loading,
.analytics-page__empty {
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;

  &--3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.kpi-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;

  span {
    font-size: 13px;
    color: #6b7a90;
  }

  strong {
    font-size: 24px;
    font-weight: 750;
    letter-spacing: -0.02em;
    line-height: 1.15;
  }

  em {
    font-style: normal;
    font-size: 12px;
    font-weight: 650;
    color: #6b7a90;
  }

  &--accent {
    background: linear-gradient(160deg, #163a52 0%, #0b4f8a 100%);
    border-color: transparent;
    color: #fff;

    span,
    em {
      color: rgba(255, 255, 255, 0.72);
    }

    strong {
      color: #fff;
    }
  }
}

.is-up {
  color: #0f766e !important;
}

.is-down {
  color: #b42318 !important;
}

.is-flat {
  color: #6b7a90 !important;
}

.kpi-card--accent {
  .is-up,
  .is-down,
  .is-flat {
    color: rgba(255, 255, 255, 0.85) !important;
  }
}

.insight-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.insight-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 14px;

  .mdi {
    font-size: 22px;
    color: #0b4f8a;
  }

  h4 {
    margin: 0 0 4px;
    font-size: 13px;
    color: #6b7a90;
    font-weight: 650;
  }

  p {
    margin: 0;
    font-size: 14px;
    font-weight: 650;
  }
}

.charts-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.panel {
  padding: 16px 18px 10px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  margin-bottom: 14px;

  &--wide {
    min-width: 0;
  }
}

.panel__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 12px;
    color: #6b7a90;
  }
}

.panel__switch {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #eef2f6;

  button {
    padding: 5px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #6b7a90;
    font-size: 12px;
    font-weight: 650;
    cursor: pointer;
    transition: all 0.15s ease;

    &.active {
      background: #fff;
      color: #0b4f8a;
      box-shadow: 0 1px 3px rgba(16, 36, 58, 0.12);
    }
  }
}

.panel__empty {
  padding: 48px 16px;
  text-align: center;
  color: #6b7a90;
  font-size: 14px;
}

.rank-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0 12px;
}

.rank-list__item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
}

.rank-list__pos {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: #0b4f8a;
  font-size: 12px;
  font-weight: 700;

  &--gold {
    background: #fff4d6;
    color: #9a6b00;
  }
}

.rank-list__body {
  min-width: 0;

  strong {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.rank-list__bar {
  height: 6px;
  border-radius: 999px;
  background: #eef2f6;
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0b4f8a, #3b82b0);
  }
}

.cashier-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cashier-list__item {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cashier-list__pos {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #eef4fa;
  color: #0b4f8a;
  font-size: 13px;
  font-weight: 750;
}

.cashier-list__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;

  strong {
    font-size: 14px;
    font-weight: 700;
  }

  small {
    color: #6b7a90;
    font-size: 12px;
  }
}

.cashier-list__bar {
  height: 6px;
  border-radius: 999px;
  background: #eef2f6;
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #1e7d3e, #4caf76);
  }
}

.cashier-list__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;

  b {
    font-size: 14px;
    font-weight: 750;
    color: #152033;
  }

  small {
    color: #6b7a90;
    font-size: 12px;
    white-space: nowrap;
  }
}

.rank-list__meta {
  text-align: right;

  b {
    display: block;
    font-size: 14px;
  }

  small {
    color: #6b7a90;
    font-size: 12px;
  }
}

.stock-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  padding-bottom: 8px;
}

.stock-chip {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff8f1;
  border: 1px solid #f3d5b5;

  strong {
    font-size: 13px;
  }

  span {
    font-weight: 750;
    color: #b45309;
  }
}

.period-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

.period-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;

  h3 {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 700;
  }

  p {
    margin: 2px 0;
    font-size: 13px;
    color: #3d4f63;
  }
}

.period-card__row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0;
  border-top: 1px solid #eef2f6;

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
}

.period-card__badge {
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e8f1f8;
  color: #0b4f8a;
  font-size: 12px;
  font-weight: 650;
}

@media (max-width: 1100px) {
  .kpi-grid,
  .insight-row,
  .charts-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 720px) {
  .analytics-page {
    padding: 16px 12px 28px;
  }

  .analytics-page__header {
    flex-direction: column;
  }

  .kpi-grid,
  .kpi-grid--3,
  .insight-row,
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .panel {
    padding: 14px;
  }

  .panel__head {
    flex-wrap: wrap;
  }

  .cashier-list__item {
    flex-wrap: wrap;
  }

  .cashier-list__meta {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 42px;
  }

  .period-grid {
    grid-template-columns: 1fr;
  }
}
</style>
